import React, { useContext, useEffect, useState } from "react";
import { usePointingContainerStore } from "../../../../store/PointingStore";
import { useSearchParams } from "react-router";
import { PointingService } from "../../../../core/_request";
import { HttpStatusCode } from "axios";
import { OperationContext } from "../../CreateOperation";
import { Loader } from "../../../../../../common/components/Loader/Loader";
import { LFTableV3 } from "../../../../../../common/components/LFTable/LFTableV3";
import { ColumnItem } from "../../../../../../common/models/table";
import { CustomDrawer } from "../../../../../../common/components/Drawer/CustomDrawer";
import { ActionsKey } from "../../../../../../common/models/Actions";
import { GlobalRequest } from "../../../../../../common/services/request";
import { ContainerDetails } from "../details/containerDetails/ContainerDetails";
import { useOperationType } from "../../../../core/_context";
import { getStatusTagData } from "../../../../../../utilities/StatusTag";
import { Tag } from "antd";
import { API_URLs } from "../../../../../../utilities/API_URLs";

export const LandingContainerPointingList = () => {
  const { initialValue } = useContext(OperationContext);

  const {
    list,
    pageState,
    totalItems,
    setList,
    resetPageState,
    setPageState,
    actioned,
    setActioned,
  } = usePointingContainerStore();
  const [searchParams] = useSearchParams();
  const movement = searchParams.get("movment");
  const type = searchParams.get("type");

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowDrawer, setIsShowDrawer] = useState<boolean>(false);

  const operationType = useOperationType();

  const cols: ColumnItem[] = [
    {
      key: "reference",
      title: "Référence",
      dataIndex: "reference",
      searchKey: "uniqueIdentifier",
      searchType: "text",
      show: true,
    },
    ...(operationType === "sales" ? [
      {
        key: "blNumber",
        title: "Numéro BL",
        dataIndex: "blNumber",
        show: true,
        // isCanSort: true,
      } as ColumnItem,
    ] : []),
    {
      key: "loadFillingStatus",
      title: "Status",
      dataIndex: "loadFillingStatus",
      show: true,
      // isCanSort: true,
      nestedKey: "loadFillingStatus.id",
      render: (state: any) => {
        const { label, color } = getStatusTagData(state?.id);
        return (
          <div className="h-100 w-100 d-flex align-items-center justify-content-center">
            <Tag color={color}>{label}</Tag>
          </div>
        );
      },
    },
    {
      key: "sealNumber",
      title: "N° Plomb",
      dataIndex: "sealNumber",
      show: true,
      render: (sealNumber: string, record: any) => {
        const additionalSealNumbers = record?.additionalSealNumbers ?? [];
    
        const seals = [
          sealNumber,
          ...additionalSealNumbers.map((item: any) =>
            typeof item === "string" ? item : item?.sealNumber
          ),
        ].filter(Boolean);
    
        const rows = [];
        for (let i = 0; i < seals.length; i += 3) {
          rows.push(seals.slice(i, i + 3));
        }
    
        return (
          <div className="d-flex flex-column align-items-center">
            {rows.map((row, rowIndex) => (
              <div
                key={rowIndex}
                className="d-flex justify-content-center"
                style={{ gap: "4px", marginBottom: "4px" }}
              >
                {row.map((seal, index) => (
                  <Tag key={`${seal}-${index}`}>{seal}</Tag>
                ))}
              </div>
            ))}
    
            {seals.length === 0 && "-"}
          </div>
        );
      },
    },
    {
      key: "containerType",
      title: "Type de conteneur",
      dataIndex: "containerType",
      show: true,
      searchType: "select",
      selectUri: API_URLs.settings.containerType.getContainerTypesForSelect,
      nestedKey: "containerType.abbreviation",
      render: (state: any) => {
        return (
          <div className="h-100 w-100 d-flex align-items-center justify-content-center">
            <span>{state?.abbreviation}</span>
          </div>
        );
      },
    },
    ...(operationType === "sales"
      ? [
          {
            key: "hold",
            title: "Cale",
            dataIndex: "hold",
            show: true,
          } as ColumnItem,
        ]
      : []),
    {
      key: "goodsDescription",
      title: "Disant contenir",
      dataIndex: "goodsDescription",
      show: true,
      // isCanSort: true,
    },
    ...(operationType === "sales"
      ? [
          {
            key: "inspectionOnBoardPeriod",
            title: "Pointage à Bord",
            dataIndex: "inspectionOnBoardPeriod",
            searchType: "date",
            searchKey: "inspectionOnBoardStartAt",
            show: true,
            isCanSort: true,
            sortKey: "inspectionOnBoardStartAt",
            defaultSort: "desc",
          } as ColumnItem,
        ]
      : []),

    {
      key: "inspectionDocksidePeriod",
      title: operationType === "sales" ? "Pointage à Quai" : "Pointage",
      dataIndex: "inspectionDocksidePeriod",
      show: true,
      searchType: "date",
      searchKey: "inspectionDocksideStartAt",
      isCanSort: true,
      sortKey: "inspectionDocksideStartAt",
      defaultSort: operationType === "cabotage" ? "desc" : null,
    },
    ...(operationType === "sales"
      ? [
          {
            key: "inspectionStoragePeriod",
            title: "Pointage entreposage",
            dataIndex: "inspectionStoragePeriod",
            searchType: "date",
            searchKey:"inspectionStorageStartAt",
            show: true,
            isCanSort: true,
            sortKey: "inspectionStorageStartAt"
          } as ColumnItem,
        ]
      : []),
  ];

  useEffect(() => {
    if (!isShowDrawer) {
      setActioned({ action: "no", data: null });
    }
  }, [isShowDrawer]);

  useEffect(() => {
    resetPageState();
  }, [movement, type]);

  useEffect(() => {
    const selected =
      initialValue?.debarkationMovement?.loadPossessionStatus?.find(
        (item: any) => item.id === type?.toUpperCase()
      ) ?? initialValue?.debarkationMovement?.loadPossessionStatus?.[0];

    if (selected && selected?.url && movement) {
      const hasOrder = (() => {
        if (pageState?.order?.dataKey && pageState?.order?.order) {
          return true;
        }
  
        return Object.entries(pageState).some(
          ([key, value]) =>
            key.startsWith("order[") &&
            value !== null &&
            value !== undefined &&
            value !== ""
        );
      })();
  
      if (!hasOrder && !pageState?.__defaultOrderSet) { // defaultOrderSet to avoid infinite loops
        if (operationType === "sales") {
          setPageState({
            ...pageState,
            order: {
              inspectionOnBoardStartAt: "desc",
            },
            __defaultOrderSet: true,
          });
        } else {
          setPageState({
            ...pageState,
            order: {
              inspectionDocksideStartAt: "desc",
            },
            __defaultOrderSet: true,
          });
        }
        return;
      }

      getAllMovements(`${selected?.url}/controlled`, {
        ...pageState,
        "movement.id": movement,
        isLoadControlled: true,
      });
    }
  }, [movement, pageState, type]);

  const handleShowDrawer = () => {
    setIsShowDrawer((prev: boolean) => !prev);
  };

  const getAllMovements = async (url: string, params: any) => {
    try {
      setIsLoading(true);

      const res = await PointingService.getAllMouvement(url, params);

      if (res?.status === HttpStatusCode.Ok) {
        setList(res?.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onActionClick = (item: any, action: ActionsKey) => {
    if (action === "delete") {
      // onDeleteHandler(item?.id);
    } else {
      handleShowDrawer();
      setActioned({
        data: item,
        action,
      });
    }
  };

  return (
    <div>
      <Loader isLoading={isLoading} />
      <LFTableV3
        columns={cols}
        data={list}
        params={pageState}
        onAction={onActionClick}
        isCanShow={() => true}
        onSearch={setPageState}
        onSort={setPageState}
        onResetSearch={resetPageState}
        pagination={{
          defaultCurrentPage: pageState.page,
          itemsPerPage: pageState?.itemsPerPage,
          totalItems: totalItems,
        }}
        onChangePage={setPageState}
      />
      {isShowDrawer && (
        <CustomDrawer
          isOpen={isShowDrawer}
          onClose={handleShowDrawer}
          title={`${actioned?.action === "read" ? "Détails" : "Modification"}`}
        >
          <ContainerDetails initialValue={actioned?.data} />
        </CustomDrawer>
      )}
    </div>
  );
};
